import { Store } from 'vuex';

import { AppState } from '@/store';
import { Logger } from '../logger';
import {
  SubmitOrderRequestPaymentMethod,
  CustomerDeliveryAddress,
  DeliveryEstimate,
  errorMap,
  OrderWithContext,
  Restaurant,
  IPaymentForm,
  TokenizedPaymentCard,
  GetDeliveryEstimateRequest,
} from '@/models';
import { MUTATIONS, CheckoutModuleState, OneTimePaymentForm, CheckoutSustainabilityError } from '@/store/checkout';
import { DeliveryService } from '../delivery/delivery.service';
import { OrderService } from '../order/order.service';
import { CheckoutValidationService } from './checkout-validation.service';
import { getEntreeCount } from '../../components/checkout/CmgCheckout/cmg-checkout-helpers';
import { AlternatePickupLocations } from '@/models/alternate-pickup-locations';

export type PickupField = keyof CheckoutModuleState['pickup'];
export type OneTimePaymentField = keyof OneTimePaymentForm;
export type GuestInfoField = keyof CheckoutModuleState['guest']['info'];

export interface CheckoutService extends CheckoutValidationService {
  setSelectedPickupTime(time: string): void;
  setAvailablePickupTimes(availableTimes: string[]): void;
  setPaymentProvider(providerId: string): void;
  setPaymentMethods(methods: SubmitOrderRequestPaymentMethod[]): void;
  setOneTimePayCard(card: TokenizedPaymentCard | null): void;
  setDeliveryEstimate(estimate: DeliveryEstimate): void;
  setDeliveryTip(tip: number, isCustomTip?: boolean): void;
  setDeliveryPhoneNumber(phone: string | undefined): void;
  setDeliveryName(name: string | undefined): void;
  setGuestInfoPhoneNumber(phone: string | undefined): void;
  setDeliveryInstructions(instructions: string | undefined): void;
  setIsSubmittingOrder(isSubmittingOrder: boolean): void;
  setIsOrderConfirmed(isOrderConfirmed: boolean | null): void;
  setIsOrderConfirmedError(isOrderConfirmedError: boolean): void;
  setIsDeliveryEstimateReady(isDeliveryEstimateReady: boolean): void;
  setHasEstimateError(hasEstimateError: boolean): void;
  setPaymentForm(paymentForm: IPaymentForm): void;
  setIsCardSaved(ssCardSaved: boolean): void;
  setSustainabilityError(susError: CheckoutSustainabilityError): void;
  setCrewTip(tip: number, isCustomTip?: boolean): void;
  setAlternatePickupLocations(alternatePickUpLocations: AlternatePickupLocations): void;

  refreshDeliveryInfo(runWithoutRestaurantId?: boolean): Promise<any>;
  refreshMinimumDeliveryOrderValue(): Promise<number>;

  forceInStorePayment(forced: boolean): void;

  reset(): void;
}

export class AppCheckoutService implements CheckoutService {
  constructor(
    private store: Store<AppState>,
    private validator: CheckoutValidationService,
    private delivery: DeliveryService,
    private order: OrderService,
    private logger: Logger,
  ) {}

  get isGuest(): boolean {
    const user = this.store.state.customer.profile;
    if (!user) {
      return true;
    }

    return user.isGuest;
  }

  get restaurant(): Restaurant | null {
    return this.store.state.restaurant.selectedRestaurant;
  }

  validate(isAboveStorePayment: boolean) {
    return this.validator.validate(isAboveStorePayment);
  }

  validatePickup(fields?: PickupField[]) {
    return this.validator.validatePickup(fields);
  }

  validateGuestInfo(fields?: GuestInfoField[], isMobile: boolean = false) {
    return this.validator.validateGuestInfo(fields, isMobile);
  }

  setSelectedPickupTime(time: string) {
    this.store.commit(MUTATIONS.SET_SELECTED_PICKUP_TIME, time);
  }

  setAvailablePickupTimes(availableTimes: string[]): void {
    this.store.commit(MUTATIONS.SET_AVAILABLE_PICKUP_TIMES, availableTimes);
  }

  setPaymentMethods(methods: SubmitOrderRequestPaymentMethod[]) {
    this.store.commit(MUTATIONS.SET_PAYMENT_METHODS, methods);
  }

  setPaymentProvider(providerId: string) {
    this.store.commit(MUTATIONS.SET_PAYMENT_PROVIDER, providerId);
  }

  setOneTimePayCard(card: TokenizedPaymentCard | null) {
    this.store.commit(MUTATIONS.SET_ONE_TIME_PAYMENT_CARD, card);
  }

  setDeliveryEstimate(estimate: DeliveryEstimate) {
    this.store.commit(MUTATIONS.SET_DELIVERY_ESTIMATE, estimate);
  }

  setDeliveryTip(tip: number, isCustomTip: boolean = false) {
    this.store.commit(MUTATIONS.SET_DELIVERY_TIP, tip);
    this.store.commit(MUTATIONS.SET_IS_CUSTOM_TIP, isCustomTip);
  }

  setDeliveryPhoneNumber(phone: string | undefined): void {
    this.store.commit(MUTATIONS.SET_DELIVERY_PHONE_NUMBER, phone);
  }

  setGuestInfoPhoneNumber(phone: string | undefined): void {
    this.store.commit(MUTATIONS.SET_GUEST_INFO_PHONE_NUMBER, phone);
  }

  setDeliveryName(name: string | undefined): void {
    this.store.commit(MUTATIONS.SET_DELIVERY_NAME, name);
  }

  setDeliveryInstructions(instructions: string): void {
    this.store.commit(MUTATIONS.SET_DELIVERY_INSTRUCTIONS, instructions);
  }

  setIsSubmittingOrder(isSubmittingOrder: boolean) {
    this.store.commit(MUTATIONS.SET_IS_SUBMITTING_ORDER, isSubmittingOrder);
  }

  setIsOrderConfirmed(isOrderConfirmed: boolean | null) {
    this.store.commit(MUTATIONS.SET_IS_ORDER_CONFIRMED, isOrderConfirmed);
  }

  setIsOrderConfirmedError(isOrderConfirmedError: boolean) {
    this.store.commit(MUTATIONS.SET_IS_ORDER_CONFIRMED_ERROR, isOrderConfirmedError);
  }

  setIsDeliveryEstimateReady(isDeliveryEstimateReady: boolean) {
    this.store.commit(MUTATIONS.SET_IS_DELIVERY_ESTIMATE_READY, isDeliveryEstimateReady);
  }
  
  setHasEstimateError(hasEstimateError: boolean) {
    this.store.commit(MUTATIONS.SET_HAS_ESTIMATE_ERROR, hasEstimateError);
  }

  setEstimateErrorMessage(estimateErrorMessage: string) {
    this.store.commit(MUTATIONS.SET_ESTIMATE_ERROR_MESSAGE, estimateErrorMessage);
  }

  setPaymentForm(paymentForm: IPaymentForm) {
    this.store.commit(MUTATIONS.SET_PAYMENT_FROM, paymentForm);
  }

  setIsCardSaved(isCardSaved: boolean) {
    this.store.commit(MUTATIONS.SET_IS_CARD_SAVED, isCardSaved);
  }

  setSustainabilityError(susError: CheckoutSustainabilityError) {
    this.store.commit(MUTATIONS.SET_SUSTAINABILITY_ERROR, susError);
  }

  setCrewTip(tip: number, isCustomTip: boolean = false) {
    this.store.commit(MUTATIONS.SET_CREW_TIP, tip);
    this.store.commit(MUTATIONS.SET_IS_CUSTOM_CREW_TIP, isCustomTip);
  }

  setAlternatePickupLocations(alternatePickUpLocations: AlternatePickupLocations) {
    this.store.commit(MUTATIONS.SET_ALTERNATE_PICKUP_LOCATIONS, alternatePickUpLocations);
  }

  async refreshDeliveryInfo(runWithoutRestaurantId: boolean = false): Promise<any> {
    const deliveryAddress = this.store.state.order.selectedDeliveryAddress;
    if (!deliveryAddress) {
      this.logger.info('No delivery address set but updateDeliveryInfo was called');
      return;
    }

    const order = this.order.getCurrentOrder();
    if (!order) {
      this.logger.unreachable('Somehow there is no current order!');
      return;
    }

    await this.updateDeliveryEstimate(order, deliveryAddress, runWithoutRestaurantId);

    if (this.isGuest) {
      this.updateGuestDeliveryInfo();
    }
  }

  async refreshMinimumDeliveryOrderValue(): Promise<any> {
    try {
      const value = this.delivery.getMinimumOrderValueForDelivery();
      this.setMinimumDeliveryOrderValue(value);

      return value;
    } catch (e: any) {
      this.logger.error('Something went wrong refreshing minimum delivery order value', { error: e });
    }
  }

  forceInStorePayment(force: boolean) {
    this.store.commit(MUTATIONS.SET_FORCE_IN_STORE_PAYMENT, force);

    // If we're forcing in-store payment, clear payment methods
    if (force) {
      this.setPaymentMethods([]);
    }
  }

  reset() {
    this.store.commit(MUTATIONS.RESET_STATE);
  }

  private setMinimumDeliveryOrderValue(value: number) {
    this.store.commit(MUTATIONS.SET_MINIMUM_DELIVERY_ORDER_VALUE, value);
  }

  private updateGuestDeliveryInfo() {
    const deliveryAddress = this.store.state.order.selectedDeliveryAddress;
    if (!deliveryAddress) {
      this.logger.unreachable('Somehow no delivery address is set but the order is a delivery order');
      return;
    }

    const info = this.store.state.checkout.guest.info;
    info.customerName.value = info.customerName.value || deliveryAddress.customerName || '';
    info.phoneNumber.value = info.phoneNumber.value || deliveryAddress.phoneNumber || '';
  }

  private async updateDeliveryEstimate(order: OrderWithContext, deliveryAddress: CustomerDeliveryAddress, runWithoutRestaurantId: boolean = false) {
    this.setIsDeliveryEstimateReady(false);
    this.setHasEstimateError(false);

    const promises = [];

    promises.push(
      (async () => {
        const { minimumOrderValue } = this.store.state.checkout.delivery;

        if (minimumOrderValue === null) {
          await this.refreshMinimumDeliveryOrderValue();
        }
      })(),
    );

    promises.push(
      (async () => {
        if (!this.restaurant) {
          return;
        }

        try {
          let existingEstimate = this.store.state.checkout.delivery.estimate;
          let deliveryEstimateRequest: GetDeliveryEstimateRequest = {
            dropOffAddress: {
              addressLine1: deliveryAddress.streetAddress1 || '',
              addressLine2: deliveryAddress.streetAddress2 || '',
              locality: deliveryAddress.city || '',
              administrativeArea: deliveryAddress.state || '',
              postalCode: deliveryAddress.postalCode || '',
              countryCode: this.store.state.ui.countryProfile.countryCode,
              longitude: deliveryAddress.longitude,
              latitude: deliveryAddress.latitude
            },
            entreeCount: getEntreeCount(order.order),
            orderValue: order.order.orderMealsExtendedPrice,
            orderId: order.order.orderId
          };
          if(runWithoutRestaurantId){
            if(existingEstimate && existingEstimate.restaurantId){
              deliveryEstimateRequest.restaurantId = existingEstimate.restaurantId;
            }
            
          }
          const estimate = await this.order.getDeliveryEstimate(deliveryEstimateRequest);

          this.setDeliveryEstimate(estimate);
        } catch (e: any) {
          this.setHasEstimateError(true);
          this.setEstimateErrorMessage(errorMap(e.response.data.errorCode));
          this.logger.error('Something went wrong getting delivery estimate', { error: e });
        }
      })(),
    );

    await Promise.all(promises);

    this.setIsDeliveryEstimateReady(true);
  }
}
